﻿import * as polygonClipping from 'polygon-clipping';

/**
 * Returns the cumulative scale factors applied to an element by its own
 * transform and all ancestor transforms (e.g. a scale() on a wrapping <g>),
 * relative to the owning <svg> viewport.
 *
 * Geometry helpers measure an element in its LOCAL coordinate system, which
 * ignores any surrounding/baked transforms. Multiplying local lengths by the
 * mean scale (and areas by sx*sy) yields transform-independent measurements,
 * so a design produces the same price whether it is grouped, ungrouped or has
 * its transforms baked into the coordinates.
 *
 * The editor zooms by shrinking the content svg's viewBox, so getCTM includes
 * the current zoom factor. Pass the canvas zoom to divide it back out and get
 * a zoom-independent scale in user units.
 *
 * Falls back to {sx:1, sy:1} when the CTM is unavailable (e.g. jsdom in unit
 * tests), preserving the previous behaviour.
 */
export function getElementScale(el, zoom = 1) {
  try {
    if (el && typeof el.getCTM === 'function') {
      const m = el.getCTM();
      if (m) {
        const z = (isFinite(zoom) && zoom > 0) ? zoom : 1;
        const sx = Math.hypot(m.a, m.b) / z;
        const sy = Math.hypot(m.c, m.d) / z;
        if (isFinite(sx) && isFinite(sy) && sx > 0 && sy > 0) {
          return { sx, sy };
        }
      }
    }
  } catch (e) {
    // getCTM can throw for detached/non-rendered elements; ignore and fall back.
  }
  return { sx: 1, sy: 1 };
}
export function calculateRectanglePerimeter(rect, conversionFactor, isMetric, dpi) {
  const strokeWidth = convertStrokeWidth(rect.getAttribute('stroke-width'), isMetric, dpi);
  if (strokeWidth === 0) return 0; // Skip perimeter if there's no visible stroke

  const width = parseFloat(rect.getAttribute('width')) || 0;
  const height = parseFloat(rect.getAttribute('height')) || 0;
  const perimeter = 2 * ((width * conversionFactor) + (height * conversionFactor));

  console.log(`Rectangle Perimeter: width=${width}, height=${height}, strokeWidth=${strokeWidth}, perimeter=${perimeter}`);
  return perimeter;
}

export function calculateRectangleArea(rect, pricingComplexity, isMetric, dpi) {
  const width = parseFloat(rect.getAttribute('width')) || 0;
  const height = parseFloat(rect.getAttribute('height')) || 0;

  // Apply conversion factor to width and height
  const convertedWidth = width * (isMetric ? 0.264583 : 1 / dpi);
  const convertedHeight = height * (isMetric ? 0.264583 : 1 / dpi);

  const strokeWidth = convertStrokeWidth(rect.getAttribute('stroke-width'), isMetric, dpi);

  const isThick = strokeWidth >= pricingComplexity.strokeWidthThreshold;
  const isFilled = rect.getAttribute('fill') !== 'none';

  const area = convertedWidth * convertedHeight; // Corrected area calculation
  console.log(`calculateRectangleArea: width=${width}, height=${height}, convertedWidth=${convertedWidth}, convertedHeight=${convertedHeight}, strokeWidth=${strokeWidth}, isThick=${isThick}, isFilled=${isFilled}, area=${area}`);

  if ((isFilled || isThick) && area >= pricingComplexity.minimumCutOutArea) {
    return area;
  }

  return 0; // Return 0 if it does not meet cutout criteria
}




export function calculateCircleCircumference(circle, isMetric, dpi) {
  const radius = parseFloat(circle.getAttribute('r')) || 0;
  return 2 * Math.PI * radius * (isMetric ? 0.264583 : 1 / dpi); // Adjust for mm or inches
}

export function calculateCircleArea(circle, pricingComplexity, isMetric, dpi) {
  const radius = parseFloat(circle.getAttribute('r')) || 0;
  const strokeWidth = convertStrokeWidth(circle.getAttribute('stroke-width'), pricingComplexity.isMetric, dpi);

  const isThick = strokeWidth >= pricingComplexity.strokeWidthThreshold;
  const isFilled = circle.getAttribute('fill') !== 'none';
  const area = Math.PI * Math.pow(radius * (isMetric ? 0.264583 : 1 / dpi), 2); // Adjust radius to mm/inch

  console.log(`calculateCircleArea: radius=${radius}, strokeWidth=${strokeWidth}, isThick=${isThick}, isFilled=${isFilled}, area=${area}`);

  return (isThick || isFilled) ? area : 0;
}

// How many straight segments each curve is flattened into. 16 keeps the area
// error far below a cent at the sizes we price, and costs nothing measurable.
const CURVE_SAMPLES = 16;

// Numbers, including exponents and the "1-2" form where the minus IS the
// separator. Splitting on /[ ,]+/ cannot read either.
const PATH_NUMBER = /-?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g;
const PATH_COMMAND = /[MmLlHhVvCcSsQqTtAaZz]/;
const ARGC = { m: 2, l: 2, h: 1, v: 1, c: 6, s: 4, q: 4, t: 2, a: 7, z: 0 };

function cubicPoint(p0, p1, p2, p3, t) {
  const u = 1 - t;
  return {
    x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
    y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
  };
}

function quadPoint(p0, p1, p2, t) {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
    y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
  };
}

/**
 * Turn a path's `d` into the polygon outline it actually describes.
 *
 * 🔴 BUG-209. The previous version did `command[0].toUpperCase()` and read every
 * coordinate as absolute, then handled only M/L/Z and dropped everything else -
 * not even keeping the segment's endpoint. Two consequences, both measured on
 * production 2026-10-01: the editor writes RELATIVE paths, so a 20x10 rectangle
 * came out at exactly half its area; and RP-0003's Inkscape artwork is almost
 * all cubic, so one wave path was priced at an area of exactly ZERO.
 *
 * Curves are flattened rather than dropped, relative commands are honoured, and
 * an elliptical arc is approximated by its chord WITH A WARNING - never silently,
 * because a plausible wrong number is worse than an obvious failure.
 */
function pathToPoints(d) {
  const points = [];
  let isClosed = false;
  let cur = { x: 0, y: 0 };
  let start = { x: 0, y: 0 };
  let prevCtrl = null;   // for the smooth forms s and t
  let prevType = '';

  // Tokenise into [letter, numbers...] groups.
  const groups = [];
  for (let i = 0; i < d.length; i++) {
    if (!PATH_COMMAND.test(d[i])) continue;
    let end = i + 1;
    while (end < d.length && !PATH_COMMAND.test(d[end])) end++;
    groups.push([d[i], (d.slice(i + 1, end).match(PATH_NUMBER) || []).map(Number)]);
    i = end - 1;
  }

  const push = (p) => { points.push({ x: p.x, y: p.y }); };

  for (const [letter, args] of groups) {
    const rel = letter === letter.toLowerCase();
    let type = letter.toLowerCase();

    if (type === 'z') {
      isClosed = true;
      cur = { ...start };
      prevCtrl = null;
      prevType = 'z';
      continue;
    }

    const step = ARGC[type];
    if (!step) continue;

    for (let j = 0; j + step <= args.length; j += step) {
      const a = args.slice(j, j + step);
      const ax = (v) => (rel ? cur.x + v : v);
      const ay = (v) => (rel ? cur.y + v : v);
      let next;

      if (type === 'm') {
        next = { x: ax(a[0]), y: ay(a[1]) };
        if (j === 0) start = { ...next };
        push(next);
        cur = next;
        prevCtrl = null;
        // Further coordinate pairs after a moveto are implicit linetos.
        type = 'l';
        prevType = 'm';
        continue;
      }

      if (type === 'l') {
        next = { x: ax(a[0]), y: ay(a[1]) };
        prevCtrl = null;
      } else if (type === 'h') {
        next = { x: ax(a[0]), y: cur.y };
        prevCtrl = null;
      } else if (type === 'v') {
        next = { x: cur.x, y: ay(a[0]) };
        prevCtrl = null;
      } else if (type === 'c' || type === 's') {
        let c1;
        let c2;
        if (type === 'c') {
          c1 = { x: ax(a[0]), y: ay(a[1]) };
          c2 = { x: ax(a[2]), y: ay(a[3]) };
          next = { x: ax(a[4]), y: ay(a[5]) };
        } else {
          c1 = (prevType === 'c' || prevType === 's') && prevCtrl
            ? { x: 2 * cur.x - prevCtrl.x, y: 2 * cur.y - prevCtrl.y }
            : { ...cur };
          c2 = { x: ax(a[0]), y: ay(a[1]) };
          next = { x: ax(a[2]), y: ay(a[3]) };
        }
        for (let k = 1; k <= CURVE_SAMPLES; k++) {
          push(cubicPoint(cur, c1, c2, next, k / CURVE_SAMPLES));
        }
        prevCtrl = c2;
        cur = next;
        prevType = type;
        continue;
      } else if (type === 'q' || type === 't') {
        let c1;
        if (type === 'q') {
          c1 = { x: ax(a[0]), y: ay(a[1]) };
          next = { x: ax(a[2]), y: ay(a[3]) };
        } else {
          c1 = (prevType === 'q' || prevType === 't') && prevCtrl
            ? { x: 2 * cur.x - prevCtrl.x, y: 2 * cur.y - prevCtrl.y }
            : { ...cur };
          next = { x: ax(a[0]), y: ay(a[1]) };
        }
        for (let k = 1; k <= CURVE_SAMPLES; k++) {
          push(quadPoint(cur, c1, next, k / CURVE_SAMPLES));
        }
        prevCtrl = c1;
        cur = next;
        prevType = type;
        continue;
      } else if (type === 'a') {
        // No template has ever used an arc. Approximate by the chord so the ring
        // still closes, and SAY SO - an understated area is a wrong price.
        next = { x: ax(a[5]), y: ay(a[6]) };
        console.warn(
          'calculatePathArea: elliptical arc approximated by its chord; '
          + 'the cut-out area for this path is understated.');
        prevCtrl = null;
      }

      push(next);
      cur = next;
      prevType = type;
    }
  }

  return { points, isClosed };
}

export function calculatePathArea(path, pricingComplexity, isMetric, dpi) {
  const d = path.getAttribute('d');
  if (!d) return 0;

  // 1) parse the fill-rule
  const fillRule = path.getAttribute('fill-rule') || "nonzero";

  // 2) parse the path commands into points (see pathToPoints - BUG-209)
  const { points, isClosed } = pathToPoints(d);

  // 3) Check stroke/fill
  const strokeVal = path.getAttribute('stroke-width') || "0";
  const strokeWidth = convertStrokeWidth(strokeVal, isMetric, dpi);
  const isFilled = (path.getAttribute('fill') || 'none').toLowerCase() !== 'none';
  const isThick = strokeWidth >= pricingComplexity.strokeWidthThreshold;

  // 4) If path not closed or filled => 0, unless thick stroke
  if (!( (isClosed && isFilled) || isThick )) {
    return 0;
  }

  // 5) Convert points from px => mm or in
  const pixelToMm = 0.264583;
  const pixelToIn = 1/96;
  const factor = isMetric ? pixelToMm : pixelToIn;

  let convertedPoints = points.map(pt => ({
    x: pt.x * factor,
    y: pt.y * factor
  }));

  // 6) If (closed & filled) => area from polygonClipping
  //    else => perimeter * strokeWidth
  if (isClosed && isFilled) {
    return calculatePolygonAreaFromPoints(convertedPoints, fillRule);
  }
  else {
    // perimeter
    let perimeter = 0;
    for (let i = 0; i < convertedPoints.length - 1; i++) {
      const p1 = convertedPoints[i];
      const p2 = convertedPoints[i+1];
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      perimeter += Math.sqrt(dx*dx + dy*dy);
    }
    // if there's a final close in the path, we accounted for it in isClosed => skip
    return perimeter * strokeWidth;
  }
}

export function calculatePolygonPerimeter(polygon, conversionFactor) {
  const points = polygon.getAttribute('points');
  if (!points) return 0;

  const pointPairs = points.trim().split(' ');
  let perimeter = 0;
  let previousPoint = null;

  pointPairs.forEach((pointStr, index) => {
    const [x, y] = pointStr.split(',').map(coord => parseFloat(coord) * conversionFactor);
    if (isNaN(x) || isNaN(y)) {
      console.error(`Invalid coordinate detected at point index ${index}: x=${x}, y=${y}`);
      return;
    }

    if (previousPoint) {
      const dx = x - previousPoint.x;
      const dy = y - previousPoint.y;
      perimeter += Math.sqrt(dx * dx + dy * dy);
    }
    previousPoint = { x, y };
  });

  if (pointPairs.length > 2 && previousPoint) {
    const [xStart, yStart] = pointPairs[0].split(',').map(coord => parseFloat(coord) * conversionFactor);
    const dx = xStart - previousPoint.x;
    const dy = yStart - previousPoint.y;
    perimeter += Math.sqrt(dx * dx + dy * dy);
  }

  return perimeter;
}

// export function calculatePolygonAreaFromPoints(points) {
//   // Build the polygon for polygon-clipping:
//   // polygonClipping wants an array of polygons => each polygon is an array of rings => each ring is an array of [x,y].
//   const singleRing = points.map(pt => [pt.x, pt.y]);
//   const polygon = [singleRing];         // one polygon with one ring
//   const multiPolyInput = [polygon];     // array of polygons
//
//   // Union to handle possible self-intersections or overlaps
//   const multiPolygon = polygonClipping.union(multiPolyInput);
//
//   // Shoelace area for the union result
//   const area = computeMultiPolygonArea(multiPolygon);
//
//   return area; // in whatever units your points array is in (e.g. mm)
// }
export function calculatePolygonArea(
  polygon,
  pricingComplexity,
  conversionFactor,
  isMetric,
  dpi
) {
  // 1) Read the 'points' attribute
  const pointsAttr = polygon.getAttribute('points');
  if (!pointsAttr) {
    console.error("Polygon has no 'points' attribute:", polygon);
    return 0;
  }

  // 2) Convert the raw points from strings => numeric coords
  const rawPairs = pointsAttr.trim().split(/\s+/); // split on spaces
  const points = [];
  for (let i = 0; i < rawPairs.length; i++) {
    const [xStr, yStr] = rawPairs[i].split(',');
    const xNum = parseFloat(xStr);
    const yNum = parseFloat(yStr);
    if (isNaN(xNum) || isNaN(yNum)) {
      console.error("Invalid polygon coordinate pair:", rawPairs[i]);
      continue;
    }
    points.push({
      x: xNum * conversionFactor,
      y: yNum * conversionFactor
    });
  }
  if (points.length < 3) {
    console.error("Not enough points for polygon area calculation:", points);
    return 0;
  }

  // 3) Detect fill-rule
  //    (SVG default is 'nonzero' if not specified; 'evenodd' changes how self-intersect is filled)
  const fillRule = polygon.getAttribute('fill-rule') || "nonzero";

  // 4) Check stroke
  const strokeWidthVal = polygon.getAttribute('stroke-width') || "0";
  const strokeWidth = convertStrokeWidth(strokeWidthVal, isMetric, dpi);

  const isFilled = (polygon.getAttribute('fill') || 'none').toLowerCase() !== 'none';
  const isThick = strokeWidth >= pricingComplexity.strokeWidthThreshold;

  // 5) Compute polygon perimeter for the 'thick stroke' scenario
  let perimeter = 0;
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    perimeter += Math.sqrt(dx * dx + dy * dy);
  }

  // 6) If filled => polygonClipping area
  //    else if thick => perimeter * strokeWidth
  //    else => 0
  let finalArea = 0;
  if (isFilled) {
    // 'filled' => compute using polygon-clipping
    finalArea = calculatePolygonAreaFromPoints(points, fillRule);
  }
  else if (isThick) {
    finalArea = perimeter * strokeWidth;
  }

  return finalArea;
}

 // Calculate the metrics for text elements based on fill and stroke width
export function calculateTextMetrics(textElement, pricingComplexity, conversionFactor) {
  const fontSize = parseFloat(textElement.getAttribute('font-size')) || 0;
  const strokeWidth = (parseFloat(textElement.getAttribute('stroke-width')) || 0) * conversionFactor;
  const isThick = strokeWidth >= pricingComplexity.strokeWidthThreshold;
  const isFilled = textElement.getAttribute('fill') !== 'none';

  // Calculate area for each character based on font size if it should be a cutout area
  const characterCount = textElement.textContent.length;
  const areaPerCharacter = fontSize * fontSize * conversionFactor ** 2;
  const totalArea = areaPerCharacter * characterCount;

  const cutOutArea = isThick || isFilled ? totalArea : 0;
  const lineLength = isThick || isFilled ? 0 : fontSize * characterCount * conversionFactor;

  return {
    lineLength,
    cutOutArea,
  };
}

export function convertStrokeWidth(strokeWidth, isMetric, dpi = 96) {
  const unitConversion = {
    px: 1,
    mm: 1 / 0.264583,
    cm: 1 / (0.264583 * 10),
    in: dpi,
    pt: dpi / 72,
    pc: dpi / 6
  };

  const match = strokeWidth?.toString().match(/^([\d.]+)([a-z%]*)$/i);
  if (!match) {
    console.warn("Invalid stroke-width:", strokeWidth);
    return parseFloat(strokeWidth) || 0; // Fallback to raw number or 0
  }
  const [, value, unit] = match;
  const numericValue = parseFloat(value);
  const conversionFactor = unitConversion[unit || "px"] || 1;
  return isMetric ? numericValue * conversionFactor * 0.264583 : numericValue * conversionFactor;
}

// Helper functions for ellipse and polyline calculations
export function calculateEllipsePerimeter(ellipse, conversionFactor, pricingComplexity) {
  const strokeWidth = parseFloat(ellipse.getAttribute('stroke-width')) || 0;
  if (strokeWidth < pricingComplexity.strokeWidthThreshold) {
    return 0;
  }
  const rx = parseFloat(ellipse.getAttribute('rx')) || 0;
  const ry = parseFloat(ellipse.getAttribute('ry')) || 0;
  const h = Math.pow((rx - ry), 2) / Math.pow((rx + ry), 2);
  return Math.PI * (rx + ry) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h))) * conversionFactor;
}

export function calculateEllipseArea(ellipse, pricingComplexity, strokeWidthConversion) {
  const rx = parseFloat(ellipse.getAttribute('rx')) * strokeWidthConversion || 0;
  const ry = parseFloat(ellipse.getAttribute('ry')) * strokeWidthConversion || 0;
  const area = Math.PI * rx * ry;
  const isThick = (parseFloat(ellipse.getAttribute('stroke-width')) || 0) * strokeWidthConversion >= pricingComplexity.strokeWidthThreshold;
  const isFilled = ellipse.getAttribute('fill') !== 'none';
  return (isThick || isFilled) ? area : 0;
}

export function calculatePolylineLength(polyline, conversionFactor) {
  const points = polyline.getAttribute('points');
  if (!points) return 0;

  const pointPairs = points.trim().split(' ');
  let length = 0;
  let previousPoint = null;

  pointPairs.forEach((pointStr, index) => {
    const [x, y] = pointStr.split(',').map(coord => parseFloat(coord) * conversionFactor);
    if (isNaN(x) || isNaN(y)) {
      console.error(`Invalid coordinate detected at point index ${index}: x=${x}, y=${y}`);
      return;
    }

    if (previousPoint) {
      const dx = x - previousPoint.x;
      const dy = y - previousPoint.y;
      length += Math.sqrt(dx * dx + dy * dy);
    }
    previousPoint = { x, y };
  });

  console.log(`Polyline Length: ${length}`);
  return length;
}

/**
 * Compute area for a multi-polygon result from polygon-clipping.
 * Each polygon is an array of rings, each ring is an array of [x,y].
 */
function computeMultiPolygonArea(multiPoly) {
  let totalArea = 0;
  for (const polygon of multiPoly) { // polygon = array of rings
    for (const ring of polygon) {
      totalArea += Math.abs(shoelace(ring));
    }
  }
  return totalArea;
}

/**
 * Removes consecutive duplicates from an array of [x,y].
 * Also removes the final point if it's the same as the first.
 * @param {Array<[number,number]>} ring
 * @param {number} epsilon - small threshold for float comparison
 * @returns {Array<[number,number]>} cleaned ring
 */
function removeDuplicatePoints(ring, epsilon = 1e-7) {
  if (!ring.length) return [];

  const cleaned = [ ring[0] ];
  for (let i = 1; i < ring.length; i++) {
    const [px, py] = cleaned[cleaned.length - 1];
    const [cx, cy] = ring[i];
    if (Math.abs(cx - px) > epsilon || Math.abs(cy - py) > epsilon) {
      cleaned.push([cx, cy]);
    }
  }
  // If last equals first, remove the last
  if (cleaned.length > 2) {
    const [fx, fy] = cleaned[0];
    const [lx, ly] = cleaned[cleaned.length - 1];
    if (Math.abs(fx - lx) < epsilon && Math.abs(fy - ly) < epsilon) {
      cleaned.pop();
    }
  }
  return cleaned;
}

/**
 * Shoelace formula for a single ring of coords [ [x,y], [x2,y2], ... ].
 */
function shoelace(ring) {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const j = (i + 1) % ring.length;
    sum += ring[i][0] * ring[j][1] - ring[j][0] * ring[i][1];
  }
  return sum / 2;
}


/**
 * calculatePolygonAreaFromPoints(points, fillRule):
 *   Convert an array of {x,y} to polygon-clipping format,
 *   do union or xor based on fillRule,
 *   run shoelace on the result.
 */
export function calculatePolygonAreaFromPoints(points, fillRule = "nonzero") {
  // 1) Build a ring array of [ [x,y], [x2,y2], ... ]
  let ring = points.map(pt => [pt.x, pt.y]);

  // 2) Clean duplicates
  ring = removeDuplicatePoints(ring);

  // 3) Must have >= 3 distinct points
  if (ring.length < 3) {
    console.warn("Polygon ring invalid after cleaning (fewer than 3 distinct points).");
    return 0;
  }

  // 4) Build polygon-clipping input => [ [ ring ] ]
  const polygon = [ ring ];
  const multiPolyInput = [ polygon ];

  // 5) Attempt union or xor
  let multiPolygon;
  try {
    if (fillRule.toLowerCase() === "evenodd") {
      multiPolygon = polygonClipping.xor(multiPolyInput);
    } else {
      multiPolygon = polygonClipping.union(multiPolyInput);
    }
  } catch (err) {
    console.warn("polygonClipping error, invalid geometry => returning 0:", err);
    return 0;
  }

  // 6) Shoelace total
  return computeMultiPolygonArea(multiPolygon);
}
